
import { CloseFullscreen, OpenInFull, Tune } from "@mui/icons-material";
import { Box, IconButton, Menu, Divider, MenuItem, ListItemIcon, Typography, Tooltip, createTheme } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { AppSettingsActionTypeEnum, useAppSettings } from "../../contexts/app-context/AppContext";
import { ActionMenuItem, BaseActionEnum } from "../action-menu/ActionMenuItem";
import { ViewOptionStateEnum } from "./ViewOptionStateEnum";
import MoreIcon from '@mui/icons-material/MoreVert';
import { isMobile, isTablet } from 'react-device-detect';
import { keyframes } from '@mui/material/styles';
import { useUserFavoritesContext } from "../../contexts/user-favorites-context/UserFavoritesContext";
import { useOfflineDetectContext } from "../../contexts/offline-detect-context/UseOfflineDetectContext";
import _ from "lodash";
import { useUserFavorites } from "../../queries/user-favorites/UseUserFavorites";
import { UIViewEnum } from "../../common/enums/UIViewEnum";
import { getViewIDFromPath } from "../../common/utilities/RouteHelpers";
import { useLocation } from "react-router";
import { IUIUserFavoriteRouteDetailModel } from "../../services/ui-personalization-data-service/Models";
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import { ConfigurationIcon } from "../action-menu/ActionMenuIcons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStarSharp } from "@elynx/pro-regular-svg-icons";

export type ContextActionMenuProps = {
    viewOptionState?: ViewOptionStateEnum;
    onExecuteAction?: (action: ActionMenuItem) => void;
    actions?: ActionMenuItem[];
    onOverrideViewOptionsClick?: () => void;
    hideOptionsAction?: boolean;
    hideAddToFavoritesAction?: boolean;
    buttonColor?: 'inherit' | 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
    showMaximizeViewAction?: boolean;
    maximizeView?: boolean;
    configurationActions?: ActionMenuItem[];
};

export function ContextActionMenu({
    actions,
    viewOptionState,
    onExecuteAction,
    onOverrideViewOptionsClick,
    hideOptionsAction,
    hideAddToFavoritesAction,
    buttonColor,
    showMaximizeViewAction,
    maximizeView,
    configurationActions
}: ContextActionMenuProps) {

    const [primaryActions, setPrimaryActions] = useState<ActionMenuItem[]>([]);
    const [overflowActions, setOverflowActions] = useState<ActionMenuItem[]>([]);

    const [subActionAnchorElements, setSubActionAnchorElements] = useState<HTMLButtonElement[]>([]);
    const [anchorElement, setAnchorElement] = useState<HTMLButtonElement>();
    const [configurationAnchorElement, setConfigurationAnchorElement] = useState<HTMLButtonElement>();
    const isPhone = isMobile && !isTablet;
    const [appSettings, dispatchAppSettings] = useAppSettings();
    const [, saveFavorite] = useUserFavoritesContext();
    const [offlineState] = useOfflineDetectContext();
    const location = useLocation();

    const {data: userFavorites} = useUserFavorites({});

    // const theme = useMemo(
    //     () => {
    //         return isPhone ? createTheme(appSettings.themeSet[appSettings.themeSet.mode].appHeaderV2) :
    //             createTheme(appSettings.themeSet[appSettings.themeSet.mode].main);
    //     },
    //     [appSettings.themeSet,
    //         isPhone]
    // );

    const glow = keyframes` 
    from {
        box-shadow: none;
        background-color: transparent;
    }
    to {
        box-shadow: 0 0 20px 8px rgba(41, 57, 85, .4);
        background-color: rgba(41, 57, 85, .4);
    }
    `;

    const [,,favoritesContext] = useUserFavoritesContext();
    const routeContext = useRouteContext();
    const favoriteAction = actions?.find(a => a.action === BaseActionEnum.Favorite) ??
                           actions?.flatMap(a => a.subActions)?.find(o => o?.action === BaseActionEnum.Favorite);

     
    const viewID = getViewIDFromPath(location.pathname, UIViewEnum.None);
    const routeDetail: IUIUserFavoriteRouteDetailModel = {
        uiView: viewID,
        objectID: routeContext.contextObject!.id,
        objectType: routeContext.contextObject!.objectType,
        organizationID: routeContext.organizationID,
        groupID: routeContext.groupID,
        //siteID: null,
        deviceID: routeContext.deviceID,
        tankID: routeContext.tankID,
        tagID: routeContext.tagID,
        wellID: routeContext.wellID,
        tankBatteryID: routeContext.tankBatteryID,
        configurationID: favoritesContext?.configurationID,
        //searchCriteria: null
    };

    const isFavorited = userFavorites?.find(o => o.routeDetail && 
        routeDetail.uiView === o.routeDetail.uiView  &&
        routeDetail.objectID === o.routeDetail.objectID  &&
        routeDetail.objectType === o.routeDetail.objectType &&
        routeDetail.organizationID  === (o.routeDetail.organizationID ?? undefined) &&
        routeDetail.groupID === (o.routeDetail.groupID ?? undefined) &&
        routeDetail.deviceID  === (o.routeDetail.deviceID ?? undefined) &&
        routeDetail.tankID === (o.routeDetail.tankID ?? undefined) &&
        routeDetail.tagID === (o.routeDetail.tagID ?? undefined) &&
        routeDetail.wellID === (o.routeDetail.wellID ?? undefined) &&
        routeDetail.tankBatteryID === (o.routeDetail.tankBatteryID ?? undefined) &&
        routeDetail.configurationID === (o.routeDetail.configurationID ?? undefined));

    const open = Boolean(anchorElement);
    const configurationOpen = Boolean(configurationAnchorElement);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        setAnchorElement(event.currentTarget);
    };

    const handleConfigurationClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        setConfigurationAnchorElement(event.currentTarget);
    };

    const handleClose = (item: ActionMenuItem) => {
        setAnchorElement(undefined);
        setConfigurationAnchorElement(undefined);

        if (item.action === BaseActionEnum.Favorite) {
            onAddToFavorites();
        } else {
            onExecuteAction?.(item);
        }
    };

    async function onAddToFavorites() {
        if (!isFavorited) {
            await saveFavorite();
        }
    }

    useEffect(() => {

        let overflowCount = viewOptionState === ViewOptionStateEnum.Hidden ? 3 : 2;
        //let overflowCount = 4;

        if (hideAddToFavoritesAction || favoriteAction) {
            overflowCount++;
        }

        if (actions) {
            const newActions = _.cloneDeep(actions);

            if (newActions.findLast(o => (o.subActions?.length ?? 0) > 0)) {
                //overflowCount++;
                if (hideOptionsAction) {
                    overflowCount++;
                } else {
                    overflowCount += 2;
                }
                
            }

            const primaryActions = newActions.slice(0, overflowCount);
            

            let overflow: ActionMenuItem[] = [];

            newActions.slice(overflowCount).forEach((o) => {

                if (o.subActions) {
                    overflow = overflow.concat(o.subActions);
                } else {
                    overflow.push(o);
                }
            });

            overflow.filter(o => o.action === BaseActionEnum.Excel || o.action === BaseActionEnum.PDF)
            .forEach(o => o.label = `Export ${o.label}`);


            if (isFavorited) {
                const overflowFavorite = overflow.find(o => o.action === BaseActionEnum.Favorite);
                if (overflowFavorite && overflowFavorite.icon) {

                    overflowFavorite.icon = () =>  <FontAwesomeIcon icon={faStarSharp}  
                        style={{ 
                            height: "20px", 
                            width: "20px", 
                            opacity: isFavorited ? 0.7 : undefined,

                        }} 
                        color="info"
                        />
                    
                }
            }

            if (overflow.length === 1) {
                primaryActions.push(overflow[0]);
                overflow = [];
            }

            setPrimaryActions(primaryActions);
            setOverflowActions(overflow);
        }

    }, [
        actions,
        setPrimaryActions,
        setOverflowActions,
        viewOptionState,
        hideAddToFavoritesAction,
        favoriteAction,
        isFavorited
    ]);

    return (
        <Box
            sx={{
                display: 'flex',
                flex: '1 1 auto',
                justifyContent: 'flex-end',
                marginTop: 'auto',
                marginBottom: 'auto',
                alignItems: 'center'
            }}>
            {!hideOptionsAction ? <Tooltip title='View Options'>
                <IconButton size="large"
                    aria-label="options"
                    color={viewOptionState === ViewOptionStateEnum.Changed ? 'secondary' : buttonColor ? buttonColor : isPhone ? "inherit" : 'default'}
                    edge='end'
                    sx={{ marginLeft: 'auto', visibility: viewOptionState !== ViewOptionStateEnum.Hidden ? 'visible' : 'hidden' }}
                    onClick={() => {
                        if (onOverrideViewOptionsClick) {
                            onOverrideViewOptionsClick();
                        } else {
                            dispatchAppSettings({
                                type: AppSettingsActionTypeEnum.SideSheetOpenChanged,
                                open: !appSettings.sideSheetOpen
                            });
                        }
                    }}>
                    <Tune />
                </IconButton>
            </Tooltip>
                : null}
            {!offlineState.offline && (configurationActions?.length ?? 0) > 0 
                ? <>
                    <Tooltip title='Configuration Options'>
                        <IconButton
                            size="large"
                            edge="end"
                            color={isPhone ? "inherit" : 'default'}
                            onClick={handleConfigurationClick}
                        >
                            <ConfigurationIcon />
                        </IconButton>
                    </Tooltip>

                    <Menu
                        id={"configuration-menu"}
                        color='textPrimary'
                        anchorEl={configurationAnchorElement}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'center'
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'center'
                        }}
                        keepMounted
                        open={configurationOpen}
                        onClose={handleClose}>
                        {configurationActions?.map((item, index) => [

                            item.dividerBefore && <Divider key={`db${index}`}/>,
                            <MenuItem
                                key={`mi${index}`}
                                disabled={item.disabled}
                                onClick={() => handleClose(item)}>
                                <ListItemIcon sx={{ minWidth: theme => theme.spacing(4) }}>
                                    {
                                        item.icon ? item.checked && item.checkedIcon ?
                                            <item.checkedIcon /> :
                                            <item.icon /> :
                                            undefined
                                    }
                                </ListItemIcon>
                                <Typography variant='inherit'>
                                    {item.label}
                                </Typography>
                            </MenuItem>,

                            item.dividerAfter &&
                            overflowActions.length > 1 &&
                            <Divider key={`da${index}`}/>
                        ])}
                    </Menu>
                </>
                : null
            }
            {showMaximizeViewAction ? <Tooltip title={maximizeView ? "Restore" : "Maximize"}>
                <IconButton size="large"
                    aria-label="options"
                    edge='end'
                    //sx={{ marginLeft: 'auto' }}
                    onClick={
                        () => onExecuteAction && onExecuteAction({ 
                            label: 'Maximize/Restore',
                            action: BaseActionEnum.ExpandCollapse 
                        })
                    }>
                    {maximizeView ? <CloseFullscreen fontSize="small" /> : <OpenInFull fontSize="small" />}
                </IconButton>
            </Tooltip>
                : null}
            {/* {!offlineState.offline && !hideAddToFavoritesAction && !favoriteAction ? 
            <Tooltip title='Add to Favorites'>
                <IconButton size="large"
                    aria-label="favorites"
                    edge='end'
                    //color={buttonColor ? buttonColor : isPhone ? "inherit" : 'default'}
                    color={isFavorited ? 'info' : buttonColor ? buttonColor : isPhone ? "inherit" : 'default'}
                    sx={{
                        opacity: isFavorited ? 0.7 : undefined
                    }}
                    onClick={() => onAddToFavorites()}>
                    <Star />
                </IconButton>
            </Tooltip>
                : null} */}
            {primaryActions.map((o, index) =>
                <React.Fragment key={index}>
                    <Tooltip title={o.label} key={index}>
                        <Box component='span'>
                            <IconButton
                                id={o.id}
                                size="large"
                                disabled={o.disabled}
                                aria-label={o.label}
                                edge="end"
                                color={o.action === BaseActionEnum.Favorite && isFavorited ? 'info' : buttonColor ? buttonColor : isPhone ? "inherit" : 'default'}
                                onClick={(event) => {
                                    if (o.action === BaseActionEnum.Favorite) {
                                        onAddToFavorites();
                                    } else {
                                        if (o.subActions) {
                                            setSubActionAnchorElements([...subActionAnchorElements, event.currentTarget]);
                                        } else {
                                            onExecuteAction?.(o);
                                        }
                                    }
                                }}

                                sx={{ 
                                    animation: o.pulse ? 
                                                    `${glow} 1.5s ease-in-out infinite alternate` : 
                                                    undefined ,
                                    opacity: o.action === BaseActionEnum.Favorite && isFavorited ? 0.7 : undefined                                                    
                                }}
                            >
                                <Box sx={{
                                    display: "flex",
                                    flex: "0 0 auto",
                                    border: theme => o.selected
                                        ? `solid 1px ${theme.palette.text.primary}`
                                        : "none",
                                    borderRadius: "4px"
                                }}>
                                    {<o.icon />}
                                </Box>
                            </IconButton>
                        </Box>
                    </Tooltip>
                    {o.subActions &&
                        <Menu
                            id={`subaction-menu`}
                            color='textPrimary'
                            anchorEl={subActionAnchorElements.find(v => v.id === o.id)}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'center'
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'center'
                            }}

                            keepMounted
                            open={Boolean(subActionAnchorElements.find(v => v.id === o.id))}
                            onClose={() => setSubActionAnchorElements(subActionAnchorElements.filter(v => v.id !== o.id))}
                        >
                            {o.subActions.map((item, index) => [

                                item.dividerBefore && <Divider key={`db${index}`}/>,
                                <MenuItem
                                    key={`mi${index}`}
                                    disabled={item.disabled}
                                    onClick={() => {
                                        setSubActionAnchorElements(subActionAnchorElements.filter(v => v.id !== o.id));
                                        onExecuteAction?.(item);
                                    }}>
                                    {item.icon && 
                                    <ListItemIcon id={`${item.id}`} sx={{ minWidth: theme => theme.spacing(4)}}>
                                        {item.checked && item.checkedIcon ? 
                                            <item.checkedIcon /> : 
                                                <item.icon />
                                        }
                                    </ListItemIcon>
                                    }
                                    <Typography variant='inherit'>
                                        {item.label}
                                    </Typography>
                                </MenuItem>,

                                item.dividerAfter &&
                                <Divider key={`da${index}`}/>
                            ])}
                        </Menu>
                    }
                </React.Fragment>
            )}
            {overflowActions.length > 0 &&
                <>
                    <Tooltip title='More Options'>
                        <IconButton
                            size="large"
                            aria-label="display more actions"
                            edge="end"
                            color={isPhone ? "inherit" : 'default'}
                            onClick={handleClick}>
                            <MoreIcon />
                        </IconButton>
                    </Tooltip>

                    <Menu
                        id={"more-options-menu"}
                        color='textPrimary'
                        anchorEl={anchorElement}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'center'
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'center'
                        }}

                        keepMounted
                        open={open}
                        onClose={handleClose}>
                        {overflowActions.map((item, index) => [

                            item.dividerBefore && <Divider key={`db${index}`}/>,
                            <MenuItem
                                key={`mi${index}`}
                                disabled={item.disabled}
                                onClick={() => handleClose(item)}>
                                <ListItemIcon sx={{ minWidth: theme => theme.spacing(4) }}>
                                    {
                                        item.icon ? item.checked && item.checkedIcon ? 
                                        <item.checkedIcon /> : 
                                        <item.icon /> :
                                        undefined
                                    }
                                </ListItemIcon>
                                <Typography variant='inherit'>
                                    {item.label}
                                </Typography>
                            </MenuItem>,

                            item.dividerAfter &&
                            overflowActions.length > 1 &&
                            <Divider key={`da${index}`}/>

                        ])}


                        {!offlineState.offline && !hideAddToFavoritesAction && !favoriteAction ? 


                                <MenuItem
                                    onClick={() => onAddToFavorites()}>
                                    <ListItemIcon 
                                        sx={{
                                            minWidth: theme => theme.spacing(4),
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faStarSharp}  
                                            style={{ 
                                                height: "20px", 
                                                width: "20px", 
                                                opacity: isFavorited ? 0.7 : undefined, 
                                            }} 
                                            color="info"
                                        />
                                    </ListItemIcon>
                                    <Typography variant='inherit'>
                                        Add to Favorites
                                    </Typography>
                                </MenuItem>

                            : null}



                    </Menu>
                </>
            }
        </Box>
    );
}
